import { useEffect } from "react";
import { MapPin, Clock, CalendarDays, Laptop, TrendingUp, Gift, Shield, ChevronRight } from "lucide-react";
import CTABand from "@/components/recruit/CTABand";

const WORK_STYLE = [
  { icon: Laptop, title: "リモートワーク中心", desc: "出社は週２回。自宅やカフェなど、集中できる環境で働けます。" },
  { icon: Clock, title: "フレキシブルな勤務時間", desc: "基本は10:00～19:00ですが、ライフスタイルに合わせて調整可能。成果を出すことが基本です。" },
  { icon: CalendarDays, title: "年間休日120日", desc: "完全週休２日制。しっかり休んで、しっかり成果を出す。" },
  { icon: MapPin, title: "千代田区オフィス", desc: "飯田橋グラン・ブルーム9F。出社日はアクセスの良い環境で。" },
];

const BENEFITS = [
  { icon: TrendingUp, title: "成果連動インセンティブ", desc: "四半期ごとに支給。上限なし。インセンティブは「ADR × 室数」で計算されます。" },
  { icon: Gift, title: "ADR × 室数", desc: "部屋が大きいほどADR（１泊あたりの単価）が高くなり、室数が多いほど掛け算で報酬が増えます。" },
  { icon: Shield, title: "報酬の考え方", desc: "固定給で安定を確保しつつ、成果に応じて大きく伸ばせる設計。「売れる人が正当に報われる」仕組みです。" },
];

const GROWTH_ENV = [
  { title: "成長スピードが速い環境", desc: "少人数組織だからこそ、幅広い経験を短期間で積めます。営業だけでなく、事業開発・マーケティング視点も身につきます。" },
  { title: "営業の仕組みを一緒に作る", desc: "既にあるツールや資料をベースに、あなたの経験を活かしてより良い営業プロセスを一緒に構築していきます。" },
  { title: "事業インパクトが見える", desc: "自分が開拓した物件がACOEブランドとして立ち上がり、ゲストを迎える。仕事の成果が目に見えるやりがいがあります。" },
  { title: "役員登用の可能性", desc: "マネジメントトラック・プロフェッショナルトラックの両方から、事業責任者への道が開かれています。" },
];

const MILESTONES = [
  { period: "入社～30日", title: "ラーニングフェーズ", items: ["ACOEのブランド・運営モデルの理解", "既存物件の視察・運営現場の体験", "代表と一緒に営業プロセスを設計", "初期のソーシング活動を開始"] },
  { period: "30日～60日", title: "実践フェーズ", items: ["自分なりのパイプラインが動き始める", "オーナーとの面談・提案を本格化", "何がうまくいき、何がうまくいかないかを分析", "[初成約の目安]"] },
  { period: "60日～", title: "成長フェーズ", items: ["失敗から学んだアイデアを実行し、新たなパイプラインを構築", "複数案件の同時進行", "営業プロセスの改善提案"] },
];

export default function CulturePage() {
  useEffect(() => { document.title = "制度・環境 | ACOE 採用サイト"; }, []);

  return (
    <div data-testid="culture-page">
      {/* ─── HERO ─── */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-acoe-charcoal" data-testid="culture-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-6">Culture & Environment</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15] max-w-3xl">
            ACOEで働くということ
          </h1>
          <p className="mt-8 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            柔軟な働き方と、成果に応じた報酬。優秀な人が自分らしく力を発揮できる環境を目指しています。
          </p>
        </div>
      </section>

      {/* ─── WORK STYLE ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="culture-work-style">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Work Style</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            働き方
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {WORK_STYLE.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white border border-acoe-border p-6 md:p-8 group hover:border-acoe-charcoal transition-all duration-300" data-testid={`work-style-${i}`}>
                  <Icon size={22} className="text-acoe-muted mb-4 group-hover:text-acoe-gold transition-colors" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-base text-acoe-charcoal mb-2">{item.title}</h3>
                  <p className="text-sm text-acoe-text leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-sm text-acoe-muted">月平均残業: 20時間以内</p>
        </div>
      </section>

      {/* ─── COMPENSATION ─── */}
      <section className="py-20 md:py-32 bg-acoe-charcoal" data-testid="culture-compensation">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-3">Compensation</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-4">
            評価・報酬
          </h2>
          <p className="text-white/50 mb-12 max-w-2xl">
            成果を出す人が正当に報われる仕組み。固定給の安定とインセンティブの上振れ、両方を追求できます。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {BENEFITS.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="border border-white/15 p-8 group hover:border-acoe-gold/40 transition-colors" data-testid={`benefit-${i}`}>
                  <Icon size={22} className="text-acoe-gold mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-lg text-white mb-3">{item.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 border border-white/15 p-8">
            <h3 className="font-heading font-bold text-lg text-white mb-4">インセンティブの計算方法</h3>
            <div className="bg-white/5 border border-white/10 p-6 mb-8 text-center">
              <p className="font-heading font-extrabold text-2xl md:text-3xl text-acoe-gold">ADR × 室数 = インセンティブ</p>
              <p className="text-sm text-white/60 mt-3">部屋が大きい → ADRが高い → インセンティブ増<br/>室数が多い → 掛け算で増える → インセンティブ増</p>
            </div>
            <h4 className="font-heading font-semibold text-base text-white mb-4">物件規模別の目安</h4>
            <p className="text-sm text-white/60 mb-6">同じ立地でも、部屋の広さ（ADR）と室数によってインセンティブが変わります。</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border border-white/10 p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-acoe-gold font-heading mb-3">Small</p>
                <p className="text-sm text-white/60 mb-1">5室 × 小～中サイズの部屋</p>
                <p className="text-xs text-white/60 mb-4">ADR ¥25,000～¥35,000 目安</p>
                <p className="font-heading font-extrabold text-xl text-white">¥70万～¥140万</p>
                <p className="text-xs text-white/60 mt-1">/ 棟</p>
              </div>
              <div className="border border-white/10 p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-acoe-gold font-heading mb-3">Medium</p>
                <p className="text-sm text-white/60 mb-1">15室 × 中サイズの部屋</p>
                <p className="text-xs text-white/60 mb-4">ADR ¥30,000～¥40,000 目安</p>
                <p className="font-heading font-extrabold text-xl text-white">¥250万～¥450万</p>
                <p className="text-xs text-white/60 mt-1">/ 棟</p>
              </div>
              <div className="border border-white/10 p-6">
                <p className="text-xs tracking-[0.2em] uppercase text-acoe-gold font-heading mb-3">Large</p>
                <p className="text-sm text-white/60 mb-1">30室 × 中～大サイズの部屋</p>
                <p className="text-xs text-white/60 mb-4">ADR ¥35,000～¥50,000 目安</p>
                <p className="font-heading font-extrabold text-xl text-white">¥600万～¥1,000万+</p>
                <p className="text-xs text-white/60 mt-1">/ 棟</p>
              </div>
            </div>
            <p className="text-xs text-white/60 mt-6 text-center">※ 上記は目安です。実際のインセンティブは物件条件により異なります。詳細はカジュアル面談でお話しします。</p>
          </div>
        </div>
      </section>

      {/* ─── GROWTH ENVIRONMENT ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="culture-growth">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Growth</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            成長環境
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {GROWTH_ENV.map((item, i) => (
              <div key={i} className="bg-white border border-acoe-border p-8 hover:border-acoe-charcoal transition-all duration-300" data-testid={`growth-${i}`}>
                <h3 className="font-heading font-bold text-base text-acoe-charcoal mb-3">{item.title}</h3>
                <p className="text-sm text-acoe-text leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ONBOARDING ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="culture-onboarding">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Onboarding</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-4">
            入社後のサポート
          </h2>
          <p className="text-acoe-text mb-12 max-w-2xl">
            最初の30日間は代表と一緒にACOEのブランド・運営・営業プロセスを理解するラーニング期間。一人で放り出されることはありません。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { period: "Week 1-2", title: "ブランド理解", desc: "ACOEの運営物件を視察し、ブランドの強み・ゲスト体験・運営の裏側を体感します。" },
              { period: "Week 2-3", title: "営業プロセス設計", desc: "代表と一緒に、ソーシング方法・提案資料・ターゲットリストを整理。あなたの経験も活かしながら営業の進め方を設計します。" },
              { period: "Week 3-4", title: "実践開始", desc: "初期のソーシング活動を開始。代表と相談しながら、自分のペースでパイプライン構築に着手します。" },
            ].map((step, i) => (
              <div key={i} className="border-t-2 border-acoe-gold pt-6" data-testid={`onboarding-${i}`}>
                <p className="text-sm text-acoe-gold font-heading font-semibold mb-2">{step.period}</p>
                <h3 className="font-heading font-bold text-base text-acoe-charcoal mb-2">{step.title}</h3>
                <p className="text-sm text-acoe-text leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MILESTONES ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="culture-milestones">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Growth Path</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-4">
            成功の目安
          </h2>
          <p className="text-acoe-text mb-12 max-w-2xl">
            入社後のステップを目安としてご紹介します。ペースは人それぞれ。代表と相談しながら、自分に合ったリズムで進められます。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {MILESTONES.map((m, i) => (
              <div key={i} className="relative bg-white border border-acoe-border p-6" data-testid={`milestone-${i}`}>
                <div className="h-1 w-full bg-acoe-gold/20 mb-6">
                  <div className="h-full bg-acoe-gold" style={{ width: `${(i + 1) * 33.3}%` }} />
                </div>
                <p className="text-sm text-acoe-gold font-heading font-semibold mb-2">{m.period}</p>
                <h3 className="font-heading font-bold text-lg text-acoe-charcoal mb-4">{m.title}</h3>
                <ul className="space-y-2">
                  {m.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <ChevronRight size={14} className="text-acoe-muted mt-1 flex-shrink-0" strokeWidth={2} />
                      <span className="text-sm text-acoe-text">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <CTABand variant="dark" />
    </div>
  );
}
